import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Alert, AlertTitle, Link } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useAuth, useNavigation } from "@/providers";
import { DatasetFileGrid } from "@/shared/components/DatasetFileGrid";
import { EventsList } from "@/shared/components/events";
import {
  AlertDialog,
  RobotoButton,
  RobotoTooltip,
  SelectionButton,
} from "@/shared/components/index.tsx";
import { DatasetsTable } from "@/shared/components/SearchResultViews/index.tsx";
import {
  CollectionRecord,
  CollectionResourceRef,
} from "@/shared/domain/collections";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import { EventRecord } from "@/shared/domain/events";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { FileRecord } from "@/shared/domain/files";
import {
  FileNode,
  FileSystemNode,
  isFile,
} from "@/shared/domain/files/filesystem";
import type { SelectionOption } from "@/shared/foundation/primitives/Table";
import { Condition, Dataset } from "@/types";

import styles from "./CollectionItemsTab.module.css";

interface CollectionItemsTabProps {
  collection: CollectionRecord;
  loading: boolean;
}

export const CollectionItemsTab: React.FC<CollectionItemsTabProps> = ({
  collection,
  loading,
}) => {
  const { currentOrganization } = useAuth();
  const { goto } = useNavigation();
  const queryClient = useQueryClient();

  const [removeDialogOpen, setRemoveDialogOpen] = React.useState(false);
  const [selectedItems, setSelectedItems] = React.useState<FileNode[]>([]);
  const [selectedDatasets, setSelectedDatasets] = React.useState<Set<string>>(
    new Set(),
  );

  const {
    mutate: updateCollection,
    mutateAsync: updateCollectionAsync,
    error,
  } = useUpdateCollection();

  const collectionConditions: Condition[] = React.useMemo(
    () => [
      {
        field: "collection.collection_id",
        comparator: "EQUALS" as const,
        value: collection.collection_id,
      },
    ],
    [collection.collection_id],
  );

  const eventSelectionOptions: SelectionOption<EventRecord>[] = React.useMemo(
    () => [
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }: { selected: EventRecord[] }) => {
          // Promise rejection is handled automatically with `mutate`
          updateCollection(
            {
              collectionId: collection.collection_id,
              resourceOwnerId: collection.org_id,
              updates: {
                remove_resources: selected.map((e: EventRecord) => ({
                  resource_type: "event" as const,
                  resource_id: e.event_id,
                })),
              },
            },
            {
              onSuccess: () => {
                void queryClient.invalidateQueries({
                  queryKey: eventQueryKeys.lists(),
                });
              },
            },
          );
        },
      },
    ],
    [
      collection.collection_id,
      collection.org_id,
      updateCollection,
      queryClient,
    ],
  );

  const datasets = React.useMemo(
    () =>
      "dataset" in collection.resources
        ? (collection.resources["dataset"] as Dataset[])
        : [],
    [collection.resources],
  );

  const files = React.useMemo(
    () =>
      "file" in collection.resources
        ? (collection.resources["file"] as FileRecord[]).map(
            (file) =>
              ({ type: "file", name: file.relative_path, file }) as FileNode,
          )
        : [],
    [collection.resources],
  );

  const eventCount =
    "event" in collection.resources ? collection.resources["event"].length : 0;

  const isEmpty = datasets.length + files.length + eventCount === 0;

  if (isEmpty) {
    return (
      <Alert severity="info">
        <AlertTitle>Empty Collection</AlertTitle>
        {collection.resource_type === "dataset" && (
          <>
            There are no items in this collection yet. You can add{" "}
            <Link href="/datasets" underline="hover">
              datasets
            </Link>{" "}
            to it.
          </>
        )}
        {collection.resource_type === "file" && (
          <>
            There are no items in this collection yet. You can add individual
            files to it.
          </>
        )}
        {collection.resource_type === "event" && (
          <>
            There are no items in this collection yet. You can add individual
            events to it.
          </>
        )}
      </Alert>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" style={{ marginBottom: "var(--spacing-xs)" }}>
          {error.message}
        </Alert>
      )}

      {(selectedDatasets.size + selectedItems.length > 0 ||
        files.length > 0) && (
        <div className={styles.actionsBar}>
          {selectedDatasets.size + selectedItems.length > 0 && (
            <SelectionButton
              selectedItems={selectedItems}
              handleDeselectAll={() => setSelectedItems([])}
              selectedDatasets={selectedDatasets}
              setSelectedDatasets={setSelectedDatasets}
              currentOrganization={currentOrganization}
              handleRemoveFromCollection={() => setRemoveDialogOpen(true)}
            />
          )}

          {files.length > 0 && (
            <RobotoTooltip
              title={
                selectedItems.length > 0
                  ? "Visualize selected files"
                  : "Visualize collection files"
              }
            >
              <span>
                <RobotoButton
                  variant="outlined"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => {
                    const filesToVisualize =
                      selectedItems.length > 0 ? selectedItems : files;
                    goto.visualize({
                      dataSource: {
                        files: filesToVisualize.map(
                          (item) => item.file.file_id,
                        ),
                      },
                    });
                  }}
                  {...(selectedItems.length > 0
                    ? {
                        eventName: "VisualizeCollectionFilesClicked" as const,
                        eventProperties: {
                          collectionId: collection.collection_id,
                          fileCount: selectedItems.length,
                        },
                      }
                    : {
                        eventName: "VisualizeCollectionClicked" as const,
                        eventProperties: {
                          collectionId: collection.collection_id,
                          fileCount: files.length,
                        },
                      })}
                >
                  {selectedItems.length > 0
                    ? `Visualize (${selectedItems.length})`
                    : "Visualize"}
                </RobotoButton>
              </span>
            </RobotoTooltip>
          )}
        </div>
      )}

      {datasets.length > 0 && (
        <DatasetsTable
          data={datasets}
          dataCount={datasets.length}
          loading={loading}
          selectedDatasets={selectedDatasets}
          setSelectedDatasets={setSelectedDatasets}
          variant="outlined"
        />
      )}

      {files.length > 0 && (
        <div className={styles.fileGridWrapper}>
          <DatasetFileGrid
            datasetId=""
            currentPage={files}
            gridItems={3}
            isSearching={false}
            selectedItems={selectedItems}
            handleSelectRows={(rows: FileSystemNode[], isSelected: boolean) => {
              if (isSelected) {
                const toAdd: FileNode[] = [];
                for (const row of rows) {
                  if (
                    isFile(row) &&
                    !selectedItems.some(
                      (i) => i.file.file_id === row.file.file_id,
                    )
                  ) {
                    toAdd.push(row);
                  }
                }
                setSelectedItems(selectedItems.concat(toAdd));
              } else {
                setSelectedItems(
                  selectedItems.filter(
                    (i) =>
                      !rows.some(
                        (r) => isFile(r) && r.file.file_id === i.file.file_id,
                      ),
                  ),
                );
              }
            }}
          />
        </div>
      )}

      {collection.resource_type === "event" && (
        <div className={styles.eventsTableWrapper}>
          <EventsList
            conditions={collectionConditions}
            selectionOptions={eventSelectionOptions}
            expandRows={false}
          />
        </div>
      )}

      <AlertDialog
        dialogOpen={removeDialogOpen}
        handleClose={() => setRemoveDialogOpen(false)}
        dialogTitle="Confirm Removal"
        dialogText={`Are you sure you want to remove ${selectedDatasets.size + selectedItems.length} items from this collection?`}
        dialogAction={async () => {
          const removeResources: CollectionResourceRef[] = [
            ...Array.from(selectedDatasets).map((id) => ({
              resource_type: "dataset" as const,
              resource_id: id,
            })),
            ...selectedItems.map((item) => ({
              resource_type: "file" as const,
              resource_id: item.file?.file_id || "",
            })),
          ];
          try {
            await updateCollectionAsync({
              collectionId: collection.collection_id,
              resourceOwnerId: collection.org_id,
              updates: { remove_resources: removeResources },
            });
          } finally {
            setSelectedDatasets(new Set());
            setSelectedItems([]);
          }
        }}
      />
    </>
  );
};
